"use client";

/**
 * Modal ↔ browser-history bookkeeping.
 *
 * Hub mounts the global modal stack with `syncWithUrl={false}` and drives URL
 * state itself (see `use-hub-modal-controller`). To make Back/Forward work on
 * modals we have to remember, per open modal, whether we created a browser
 * history entry for it. That bookkeeping lives here so the controller (writer)
 * and the history-sync effect (reader) share one instance per React tree.
 *
 * This is deliberately a mutable ref-style object rather than React state:
 * every consumer reads it inside effects/callbacks, and re-rendering the whole
 * app when a modal's history bookkeeping changes would be pure waste.
 *
 * ## Why the stack is authoritative and the URL is a projection
 *
 * `router.push`/`router.back()` commit on a LATER tick, and `useSearchParams`
 * reports the change a render after that. Treating the URL as the source of
 * truth therefore means every open/close decision is made against a stale
 * snapshot — which is exactly how "close a modal, immediately click another
 * row, the new modal flashes open and vanishes" happened: the pending
 * `router.back()` from the close landed *after* the new modal had opened, and
 * the sync effect faithfully reconciled the just-opened modal away.
 *
 * So: opening a modal is synchronous and unconditional against the STACK, and
 * the URL is written afterwards as a projection of that state. `pendingUrl`
 * records the projection we most recently asked the router for; while the
 * observed URL has not caught up to it, a navigation is in flight and any URL
 * the effect observes is stale, so it must not reconcile.
 */

import { createContext, useContext } from "react";
import type { ConfiguredModalId } from "../url/modal-url-config";

/** One browser-history position we own, for one open URL-configured modal. */
export interface ModalHistoryEntry {
  modalId: ConfiguredModalId;
  /**
   * The entity the modal is currently showing, so a repeat open of the SAME
   * modal on the SAME entity can be recognised as a no-op (a double-click must
   * not push a second history entry), while a switch to a different entity
   * rewrites the entry we already own instead of stacking a new one.
   */
  entityId: string | null;
  /**
   * `true` when opening this modal created a NEW history entry (`router.push`).
   * Closing it must then `router.back()` so we unwind the entry we created
   * instead of stacking a second one. `false` means the open overwrote the
   * current entry (`router.replace`) and closing rewrites it in place.
   */
  pushed: boolean;
  /**
   * The URL that was current when we pushed this entry — i.e. where a
   * `router.back()` will land. Recorded so an unwind can declare its
   * destination as `pendingUrl` up front; `back()` is the one navigation whose
   * target we cannot otherwise compute. `null` for entries we did not push
   * (deep links, `replace` opens), which unwind by rewriting in place.
   */
  urlBefore: string | null;
  /**
   * `true` once the modal has actually been observed open in the stack. Guards
   * the "user closed it via ESC/X/click-away" detection against the render tick
   * between `openModal()` and Mantine committing the open state.
   */
  observedOpen: boolean;
}

export interface ModalHistoryStore {
  /** Outermost modal first, innermost last. Mirrors the URL-configured
   *  slice of the modal stack, in the order the modals were opened. */
  entries: ModalHistoryEntry[];
  /**
   * The URL we last asked the router for, or `null` when the URL is settled.
   *
   * While this is set and the observed URL differs from it, a navigation we
   * started is still in flight — every URL the sync effect sees in that window
   * belongs to a superseded navigation and must be ignored. Cleared the moment
   * the observed URL matches (or on a route change, which resets everything).
   */
  pendingUrl: string | null;
  /**
   * Every URL that belongs to the in-flight chain but is no longer its
   * destination: the URL the chain started from, plus each target that a later
   * request superseded. Observing one of these means a navigation we have
   * already moved past finally delivered its result — stale, ignore it.
   *
   * A single "previous url" slot is not enough. Closing a modal and opening
   * another one queues TWO navigations from a THIRD starting url, so by the
   * time the close's `back()` lands there are two distinct stale urls that must
   * not be reconciled. Observing a url outside this set is the user navigating,
   * which outranks anything we have pending.
   */
  supersededUrls: string[];
}

export function createModalHistoryStore(): ModalHistoryStore {
  return { entries: [], pendingUrl: null, supersededUrls: [] };
}

/**
 * Record that we asked the router to go to `to`, observed at `observed`.
 *
 * Chains: the first request in a chain banks the url it is leaving; each later
 * one banks the target it just replaced. `useModalHistorySync` uses that set to
 * tell a stale arrival from a url the user produced.
 */
export function beginNavigation(
  store: ModalHistoryStore,
  to: string,
  observed: string
): void {
  store.supersededUrls =
    store.pendingUrl === null
      ? [observed]
      : [...store.supersededUrls, store.pendingUrl];
  store.pendingUrl = to;
}

/** Forget the in-flight chain: its result no longer decides anything. */
export function clearPendingNavigation(store: ModalHistoryStore): void {
  store.pendingUrl = null;
  store.supersededUrls = [];
}

/**
 * How the sync effect should treat the url it is currently observing.
 *
 * - `settled`    — our latest request arrived; act on it.
 * - `stale`      — a superseded request arrived; ignore it entirely.
 * - `superseding`— not ours at all, so the user navigated; drop our pending
 *                  chain and act on what they asked for.
 */
export type ObservedUrlVerdict = "settled" | "stale" | "superseding";

export function classifyObservedUrl(
  store: ModalHistoryStore,
  currentUrl: string
): ObservedUrlVerdict {
  if (store.pendingUrl === null) return "settled";
  if (currentUrl === store.pendingUrl) return "settled";
  if (store.supersededUrls.includes(currentUrl)) return "stale";
  return "superseding";
}

/**
 * Fallback for trees that use a modal controller without mounting
 * `HubModalHistoryProvider` (isolated component tests, one-off mounts).
 * Modals still open and close — they just don't participate in history.
 */
const FALLBACK_MODAL_HISTORY_STORE = createModalHistoryStore();

export const ModalHistoryContext = createContext<ModalHistoryStore>(
  FALLBACK_MODAL_HISTORY_STORE
);

export function useModalHistoryStore(): ModalHistoryStore {
  return useContext(ModalHistoryContext);
}
